import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { MultipleProgressChartData } from '../charts/MultipleProgressChart';
import { GetSalesInsightsGroupedRequest, SalesInsightsCompareGroupedResponse, useSalesInsightsCompareGrouped } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import ComparedMultipleProgressChart from '../comparedCharts/ComparedMultipleProgressChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';
import { UserContext } from '../../../../context/UserContext';

const ComparedSalesByLocation = () =>{
    const { t } = useTranslation();
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig } = useContext(DashboardContext);
    const [multipleChartData, setMultipleChartData] = useState<MultipleProgressChartData[]>([]);
    const [salesByLocationSum, setSalesByLocationSum] = useState(0);
    const [salesByLocationSumCompared, setSalesByLocationSumCompared] = useState(0);
    const { locale } = useContext(UserContext);

         const [ params, setParams ] = useState<GetSalesInsightsGroupedRequest>({
            groupBy: 'location',
            fields: 'totalSales',
            startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
            compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id,
          });

      const {
            data: salesInsightsGrouped,
            isLoading,
            isError,
            refetch: fetchComparedSalesByLocation
          } = useSalesInsightsCompareGrouped<SalesInsightsCompareGroupedResponse>(params, 'sales-insights-grouped-compare-sales-location');
        
          useEffect(() => {
                if (salesInsightsGrouped?.salesInsightsGrouped){
                    let sum = 0;
                    let sumCompared = 0;
                    setMultipleChartData(
                        salesInsightsGrouped?.salesInsightsGrouped?.map((salesInsightsObject, index) => {
                            sum += salesInsightsObject?.salesInsights?.totalSalesIncTax || 0;
                            sumCompared += salesInsightsGrouped?.comparedSalesInsightsGrouped?.[index]?.salesInsights?.totalSalesIncTax || 0

                            return {
                                key: salesInsightsObject?.key,
                                value: salesInsightsObject?.salesInsights?.totalSalesIncTax || 0,
                                comparedValue: salesInsightsGrouped?.comparedSalesInsightsGrouped?.[index]?.salesInsights?.totalSalesIncTax || 0
                            }
                        })
                    );
                    setSalesByLocationSum(sum);
                    setSalesByLocationSumCompared(sumCompared);
                }
            }, [salesInsightsGrouped]);
        
            useEffect(() => {
            setParams({
                ...params,
                startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
                compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
                compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
                locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
                locationArea: filters?.locationArea?.id,
            })
            fetchComparedSalesByLocation();
        }, [filters]);
        
        useEffect(() => {fetchComparedSalesByLocation();}, [params]);
        const currentChip: TableChip = useMemo(() => 
                ({
                    label: `${formatPeriodLabel(
                        new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                        locale
                      )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                      labelType: 'period'
                }), [filters.periodFrom, filters.periodTo]);
        
                const compareChip: TableChip = useMemo(() => 
                ({
                    label: `${formatPeriodLabel(
                        new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                        locale
                      )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                      labelType: 'periodCompare'
                }), [filters.comparePeriodFrom, filters.comparePeriodTo]);

    return (
        <Box>
            <ComparedMultipleProgressChart 
            title={t("screens.dashboarding.charts.salesByLocation")}
            currency={salesInsightsGrouped?.currency}
            description={t("screens.dashboarding.charts.includingTax")}
            isLoading={isLoading}
            data={multipleChartData}
            isError={isError}
            sum={salesByLocationSum}
            comparedSum={salesByLocationSumCompared}
            textLink={t("screens.dashboarding.charts.salesOverview")} 
            linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesByLocation.aspx`}
            chips={[currentChip, compareChip]}
            />
        </Box>
    )
}

export default ComparedSalesByLocation;
